import { groupBy, upperFirst } from "lodash-es";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActiveFilterBar } from "~/components/Filter/ActiveFilterBar/ActiveFilterBar";
import {
  ActiveFilterButton,
  ActiveFilterButtonProps,
} from "~/components/Filter/ActiveFilterButton/ActiveFilterButton";
import {
  FilterBar,
  FilterBarProps,
} from "~/components/Filter/FilterBar/FilterBar";
import {
  FilterForm,
  FilterFormProps,
} from "~/components/Filter/FilterForm/FilterForm";
import { QuickFilterBar } from "~/components/Filter/QuickFilterBar/QuickFilterBar";
import {
  QuickFilterButton,
  QuickFilterButtonProps,
} from "~/components/Filter/QuickFilterButton/QuickFilterButton";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import {
  AggregateScoreState,
  AggregateScoreType,
  AggScoresFilterType,
  GetAggregateScoresQueryVariables,
  ScoreRating,
  useGetAggregateScoresSuggestionsLazyQuery,
} from "~/operations";
import { FilterMenuOption } from "~/components/Filter/FilterMenu";
import { usePaginate } from "~/components/pagination/usePaginate";
import { AdjustedAggregateScoreType } from "../types";
import {
  ALL_FIREWATCH_SEARCH_PARAMS,
  FIREWATCH_SEARCH_PARAMS,
} from "../lib/firewatchSearchParams";
import { excludeFilterByKey } from "~/lib/filterUtils";
import {
  emptyMenuOption,
  loadingMenuOption,
} from "~/components/Filter/QuickFilterMenu/QuickFilterMenu";

export type FirewatchFilterVariables = Pick<
  NonNullable<GetAggregateScoresQueryVariables["filter"]>,
  "policyMrn" | "rating" | "risks" | "state" | "queryTerms" | "platforms"
>;
export type UseFirewatchFilterContextParams = {
  scopeMrn: string;
  pageType: AdjustedAggregateScoreType;
  paginate: ReturnType<typeof usePaginate>;
  baseQueryFilters: GetAggregateScoresQueryVariables["filter"];
};

export type UseFirewatchFilterContextOutput = {
  filterVariables: FirewatchFilterVariables;
  filterForm: ReactNode;
  filterBar: ReactNode;
  quickFilterBar: ReactNode;
  activeFilterBar: ReactNode;
  activeFilterCount: number;
  resetFilters: () => void;
};

export function useFirewatchFilterContext({
  scopeMrn,
  pageType,
  paginate,
  baseQueryFilters,
}: UseFirewatchFilterContextParams): UseFirewatchFilterContextOutput {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // search text typed into search bar
  const [searchInput, setSearchInput] = useState("");

  const typeFilterParams = searchParams.getAll(FIREWATCH_SEARCH_PARAMS.TYPE);
  const riskRatingFilterParams = searchParams.getAll(
    FIREWATCH_SEARCH_PARAMS.RISK_RATING,
  );
  const riskFactorFilterParams = searchParams.getAll(
    FIREWATCH_SEARCH_PARAMS.RISK_FACTOR_MRN,
  );
  const policyFilterParam = searchParams.get(
    FIREWATCH_SEARCH_PARAMS.POLICY_MRN,
  );
  const stateFilterParam = searchParams.get(FIREWATCH_SEARCH_PARAMS.STATE);
  const platformFilterParams = searchParams.getAll(
    FIREWATCH_SEARCH_PARAMS.PLATFORM,
  );
  const searchFilterParams = searchParams.getAll(
    FIREWATCH_SEARCH_PARAMS.QUERY_TERM,
  );
  const typeFilters = typeFilterParams.length ? typeFilterParams : undefined;
  const riskRatingFilters = riskRatingFilterParams.every((riskFactorParam) =>
    isScoreRating(riskFactorParam),
  )
    ? riskRatingFilterParams
    : undefined;
  const riskFactorFilters = riskFactorFilterParams.length
    ? riskFactorFilterParams
    : undefined;
  const platformFilters = platformFilterParams.length
    ? platformFilterParams
    : undefined;
  const policyFilter = policyFilterParam ? policyFilterParam : undefined;
  const stateFilter = isAggregateScoreState(stateFilterParam)
    ? stateFilterParam
    : undefined;
  const searchFilters = searchFilterParams.length
    ? searchFilterParams
    : undefined;

  const filtersForSuggestions: GetAggregateScoresQueryVariables["filter"] =
    useMemo(() => {
      return {
        ...baseQueryFilters,
        ...(typeFilters?.length
          ? { scoreTypes: typeFilters as AggregateScoreType[] }
          : {}),
        ...(riskRatingFilters?.length ? { rating: riskRatingFilters } : {}),
        ...(riskFactorFilters?.length
          ? { risks: { mrns: { or: riskFactorFilters } } }
          : {}),
        ...(platformFilters?.length ? { platforms: platformFilters } : {}),
        ...(policyFilter ? { policyMrn: policyFilter } : {}),
        ...(stateFilter ? { state: stateFilter } : {}),
        ...(searchFilters?.length ? { queryTerms: searchFilters } : {}),
      };
    }, [
      baseQueryFilters,
      typeFilterParams,
      riskRatingFilterParams,
      riskFactorFilterParams,
      platformFilterParams,
      policyFilterParam,
      stateFilterParam,
      searchFilterParams,
    ]);

  const isInitiative = (baseQueryFilters?.initiativeMrns?.length ?? 0) > 0;
  const shouldShowTypeFilter =
    (pageType === "AllSecurityFindings" || pageType === "AssetAllFindings") &&
    !isInitiative;

  const typeSuggestions = useFirewatchTypeSuggestions("", pageType);
  // Exclude the facet itself from filters when fetching its suggestions
  const filtersExcludingRating = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["rating"]),
    [filtersForSuggestions],
  );
  const riskRatingSuggestions = useFirewatchRiskRatingSuggestions(
    scopeMrn,
    filtersExcludingRating,
  );
  const filtersExcludingRisks = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["risks"]),
    [filtersForSuggestions],
  );
  const riskFactorSuggestions = useFirewatchRiskFactorSuggestions(
    scopeMrn,
    pageType,
    filtersExcludingRisks,
  );
  const filtersExcludingPolicy = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["policyMrn"]),
    [filtersForSuggestions],
  );
  const policySuggestions = useFirewatchPolicySuggestions(
    scopeMrn,
    pageType,
    filtersExcludingPolicy,
  );
  const filtersExcludingState = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["state"]),
    [filtersForSuggestions],
  );
  const stateSuggestions = useFirewatchStateSuggestions(
    scopeMrn,
    filtersExcludingState,
  );
  const filtersExcludingPlatforms = useMemo(
    () => excludeFilterByKey(filtersForSuggestions, ["platforms"]),
    [filtersForSuggestions],
  );
  const platformSuggestions = useFirewatchPlatformSuggestions(
    scopeMrn,
    filtersExcludingPlatforms,
  );

  const addFilter = useCallback(
    (param: string, value: string, multiple = false) => {
      const url = paginate.resetCursorUrl(location.href);

      if (multiple) {
        const existing = url.searchParams.getAll(param);
        if (!existing.includes(value)) url.searchParams.append(param, value);
      } else {
        url.searchParams.set(param, value);
      }
      navigate({ pathname: url.pathname, search: url.search });
    },
    [navigate, paginate],
  );

  const removeFilter = useCallback(
    (param: string, value?: string) => {
      const url = paginate.resetCursorUrl(location.href);
      if (value == null) {
        url.searchParams.delete(param);
      } else {
        const kept = url.searchParams.getAll(param).filter((v) => v !== value);
        url.searchParams.delete(param);
        kept.forEach((v) => url.searchParams.append(param, v));
      }
      navigate({ pathname: url.pathname, search: url.search });
    },
    [navigate, paginate],
  );

  const resetFilters = useCallback(() => {
    const url = paginate.resetCursorUrl(location.href);
    ALL_FIREWATCH_SEARCH_PARAMS.forEach((p) => url.searchParams.delete(p));
    navigate({ pathname: url.pathname, search: url.search });
  }, [navigate, paginate]);

  const activeFilters = [
    typeFilters
      ? typeFilters.map((typeFilter) => {
          return {
            label: "Type",
            value: formatTypeLabel(typeFilter),
            onClick: () =>
              removeFilter(FIREWATCH_SEARCH_PARAMS.TYPE, typeFilter),
          };
        })
      : [],
    riskRatingFilters
      ? riskRatingFilters.map((riskRatingFilter) => {
          return {
            label: "Risk Rating",
            value: formatRiskRatingLabel(riskRatingFilter),
            onClick: () =>
              removeFilter(
                FIREWATCH_SEARCH_PARAMS.RISK_RATING,
                riskRatingFilter,
              ),
          };
        })
      : [],
    riskFactorFilters
      ? riskFactorFilters.map((riskFactorFilter) => {
          return {
            label: "Risk Factor",
            value: formatRiskFactorLabel(riskFactorFilter),
            onClick: () =>
              removeFilter(
                FIREWATCH_SEARCH_PARAMS.RISK_FACTOR_MRN,
                riskFactorFilter,
              ),
          };
        })
      : [],
    platformFilters
      ? platformFilters.map((platformFilter) => {
          return {
            label: "Platform",
            value: platformFilter,
            onClick: () =>
              removeFilter(FIREWATCH_SEARCH_PARAMS.PLATFORM, platformFilter),
          };
        })
      : [],
    policyFilter
      ? {
          label: "Policy",
          value: formatPolicyLabel(policyFilter),
          onClick: () => removeFilter(FIREWATCH_SEARCH_PARAMS.POLICY_MRN),
        }
      : [],
    // stateFilter
    //   ? {
    //       label: "State",
    //       value: formatStateLabel(stateFilter),
    //       onClick: () => removeFilter("state"),
    //     }
    //   : [],
    searchFilters
      ? searchFilters.map((searchFilter) => {
          return {
            label: "Search",
            value: searchFilter,
            onClick: () =>
              removeFilter(FIREWATCH_SEARCH_PARAMS.QUERY_TERM, searchFilter),
          };
        })
      : [],
  ].flatMap((f) => f);

  const [focusedField, setFocusedField] = useState<FilterMenuOption>();

  const fieldOptions = useFirewatchSuggestions(
    focusedField?.field || "",
    searchInput,
    scopeMrn,
    pageType,
    filtersForSuggestions,
  );

  useEffect(() => {
    if ("fetchSuggestions" in fieldOptions) {
      fieldOptions.fetchSuggestions();
    }
  }, [focusedField?.field]);

  const filterBar = renderFilterBar({
    fieldOptions: fieldOptions.suggestions,
    focusedField,
    inputValue: searchInput,
    onInputValueChange: (value: string) => {
      setSearchInput(value);
      if ("fetchSuggestions" in fieldOptions) {
        fieldOptions.fetchSuggestions(value);
      }
    },
    onFocusedRemove: () => {
      setFocusedField(undefined);
    },
    onFilterSelect: (filter) => {
      setSearchInput("");
      if (!focusedField) {
        setFocusedField(filter);
      } else {
        setFocusedField(undefined);
        if (filter.param) {
          addFilter(filter.param, filter.value, filter.isMultiple);
        }
      }
    },
    onFilterCreate: (filter) => {
      setFocusedField(undefined);
      setSearchInput("");
      addFilter(FIREWATCH_SEARCH_PARAMS.QUERY_TERM, filter, true);
    },
  });

  const quickMenuOptions = (param: string, options: FilterMenuOption[]) => {
    return options.map((o) => ({
      ...o,
      onSelect: () => addFilter(param, o.value, o.isMultiple),
    }));
  };

  const quickFilterBar = renderQuickFilterBar(
    [
      shouldShowTypeFilter
        ? {
            label: "Type",
            activeCount: typeFilters?.length || 0,
            menuOptions: quickMenuOptions(
              FIREWATCH_SEARCH_PARAMS.TYPE,
              typeSuggestions.suggestions,
            ),
            isLoading: typeSuggestions.loading,
          }
        : undefined,
      {
        label: "Risk Rating",
        activeCount: riskRatingFilters?.length || 0,
        menuOptions: quickMenuOptions(
          FIREWATCH_SEARCH_PARAMS.RISK_RATING,
          riskRatingSuggestions.suggestions,
        ),
        isLoading: riskRatingSuggestions.loading,
        onMenuOpen: riskRatingSuggestions.fetchSuggestions,
      },
      {
        label: "Risk Factor",
        activeCount: riskFactorFilters?.length || 0,
        menuOptions: quickMenuOptions(
          FIREWATCH_SEARCH_PARAMS.RISK_FACTOR_MRN,
          riskFactorSuggestions.suggestions,
        ),
        isLoading: riskFactorSuggestions.loading,
        onMenuOpen: riskFactorSuggestions.fetchSuggestions,
      },
      {
        label: "Platform",
        activeCount: platformFilters?.length || 0,
        menuOptions: quickMenuOptions(
          FIREWATCH_SEARCH_PARAMS.PLATFORM,
          /* TODO: it suppose to be "platformSuggestions.suggestions," but use consistent "platform.value" as labels until we can apply
              friendly name mappings for proper spacing/capitalization */
          platformSuggestions.suggestions.map((s) => ({
            ...s,
            label: s.value,
          })),
        ),
        isLoading: platformSuggestions.loading,
        onMenuOpen: platformSuggestions.fetchSuggestions,
      },
      pageType === "AllSecurityFindings" ||
      pageType === "AssetAllFindings" ||
      pageType === AggregateScoreType.Check
        ? {
            label: "Policy",
            activeCount: policyFilter ? 1 : 0,
            menuOptions: quickMenuOptions(
              FIREWATCH_SEARCH_PARAMS.POLICY_MRN,
              policySuggestions.suggestions,
            ),
            isLoading: policySuggestions.loading,
            onMenuOpen: policySuggestions.fetchSuggestions,
          }
        : undefined,
      // {
      //   label: "State",
      //   activeCount: stateFilter ? 1 : 0,
      //   menuOptions: quickMenuOptions("state", stateSuggestions.suggestions),
      //   onMenuOpen: stateSuggestions.fetchSuggestions,
      // },
    ].flatMap((f) => f ?? []),
  );

  const activeFilterBar = renderActiveFilterBar(activeFilters, resetFilters);

  const activeFilterCount = activeFilters.length;

  const filterForm = renderFilterForm({
    filterBar,
    quickFilterBar,
    activeFilterBar,
  });

  const filterVariables: GetAggregateScoresQueryVariables["filter"] =
    useMemo(() => {
      return {
        ...(typeFilters?.length
          ? { scoreTypes: typeFilters as AggregateScoreType[] }
          : {}),
        ...(riskRatingFilters?.length ? { rating: riskRatingFilters } : {}),
        ...(riskFactorFilters?.length
          ? { risks: { mrns: { or: riskFactorFilters } } }
          : {}),
        ...(platformFilters?.length ? { platforms: platformFilters } : {}),
        ...(policyFilter ? { policyMrn: policyFilter } : {}),
        ...(stateFilter ? { state: stateFilter } : {}),
        ...(searchFilters?.length ? { queryTerms: searchFilters } : {}),
      };
    }, [
      typeFilterParams,
      riskRatingFilterParams,
      riskFactorFilterParams,
      platformFilterParams,
      policyFilterParam,
      stateFilterParam,
      searchFilterParams,
    ]);

  return {
    filterVariables,
    filterForm,
    filterBar,
    quickFilterBar,
    activeFilterBar,
    activeFilterCount,
    resetFilters,
  };
}

function renderFilterBar(props: FilterBarProps) {
  return <FilterBar {...props} />;
}

function renderQuickFilterBar(quickFilters: QuickFilterButtonProps[]) {
  return (
    <QuickFilterBar>
      {quickFilters.map((quickFilterProps) => {
        const key = quickFilterProps.label;
        return <QuickFilterButton key={key} {...quickFilterProps} />;
      })}
    </QuickFilterBar>
  );
}

function renderActiveFilterBar(
  activeFilters: ActiveFilterButtonProps[],
  onResetClick: () => void,
) {
  const items = Object.entries(groupBy(activeFilters, "label")).flatMap(
    ([label, values], index) => {
      const buttons = values.map((props) => {
        const key = props.label + props.value;
        return <ActiveFilterButton key={key} {...props} />;
      });
      const separator = (
        <ChevronRightIcon
          key={label + "-separator"}
          sx={{ fontSize: "16px", color: "text.secondary" }}
        />
      );
      return [index === 0 ? [] : separator, ...buttons];
    },
  );
  return (
    items.length > 0 && (
      <ActiveFilterBar onResetClick={onResetClick}>{items}</ActiveFilterBar>
    )
  );
}

function renderFilterForm(props: FilterFormProps) {
  return <FilterForm {...props} />;
}

function useFirewatchSuggestions(
  field: string,
  searchInput: string,
  scopeMrn: string,
  pageType: AdjustedAggregateScoreType,
  baseQueryFilters: GetAggregateScoresQueryVariables["filter"],
) {
  const categorySuggestions = useFirewatchCategorySuggestions(
    searchInput,
    pageType,
    baseQueryFilters,
  );
  const typeSuggestions = useFirewatchTypeSuggestions(searchInput, pageType);
  const filtersExcludingRating = useMemo(() => {
    const { rating, ...rest } = baseQueryFilters || {};
    return rest;
  }, [baseQueryFilters]);
  const riskRatingSuggestions = useFirewatchRiskRatingSuggestions(
    scopeMrn,
    filtersExcludingRating,
  );
  const filtersExcludingRisks = useMemo(() => {
    const { risks, ...rest } = baseQueryFilters || {};
    return rest;
  }, [baseQueryFilters]);
  const riskFactorSuggestions = useFirewatchRiskFactorSuggestions(
    scopeMrn,
    pageType,
    filtersExcludingRisks,
  );
  const filtersExcludingPolicy = useMemo(() => {
    const { policyMrn, ...rest } = baseQueryFilters || {};
    return rest;
  }, [baseQueryFilters]);
  const policySuggestions = useFirewatchPolicySuggestions(
    scopeMrn,
    pageType,
    filtersExcludingPolicy,
  );
  // const stateSuggestions = useFirewatchStateSuggestions(
  //   scopeMrn,
  //   pageType,
  // );
  const filtersExcludingPlatforms = useMemo(() => {
    const { platforms, ...rest } = baseQueryFilters || {};
    return rest;
  }, [baseQueryFilters]);
  const platformSuggestions = useFirewatchPlatformSuggestions(
    scopeMrn,
    filtersExcludingPlatforms,
  );

  switch (field) {
    case FIREWATCH_SEARCH_PARAMS.TYPE:
      return typeSuggestions;
    case FIREWATCH_SEARCH_PARAMS.RISK_RATING:
      return riskRatingSuggestions;
    case FIREWATCH_SEARCH_PARAMS.RISK_FACTOR_MRN:
      return riskFactorSuggestions;
    case FIREWATCH_SEARCH_PARAMS.POLICY_MRN:
      return policySuggestions;
    // case FIREWATCH_SEARCH_PARAMS.STATE:
    //   return stateSuggestions;
    case FIREWATCH_SEARCH_PARAMS.PLATFORM:
      return platformSuggestions;
    default:
      return categorySuggestions;
  }
}

function useFirewatchCategorySuggestions(
  searchInput: string,
  pageType: AdjustedAggregateScoreType,
  baseQueryFilters?: GetAggregateScoresQueryVariables["filter"],
) {
  const typeFilterGroup: FilterMenuOption = {
    field: FIREWATCH_SEARCH_PARAMS.TYPE,
    label: "Type",
    value: FIREWATCH_SEARCH_PARAMS.TYPE,
  };

  const riskRatingFilterGroup: FilterMenuOption = {
    field: FIREWATCH_SEARCH_PARAMS.RISK_RATING,
    label: "Risk Rating",
    value: FIREWATCH_SEARCH_PARAMS.RISK_RATING,
  };

  const riskFactorFilterGroup: FilterMenuOption = {
    field: FIREWATCH_SEARCH_PARAMS.RISK_FACTOR_MRN,
    label: "Risk Factor",
    value: FIREWATCH_SEARCH_PARAMS.RISK_FACTOR_MRN,
  };

  const policyFilterGroup: FilterMenuOption = {
    field: FIREWATCH_SEARCH_PARAMS.POLICY_MRN,
    label: "Policy",
    value: FIREWATCH_SEARCH_PARAMS.POLICY_MRN,
  };

  const platformFilterGroup: FilterMenuOption = {
    field: FIREWATCH_SEARCH_PARAMS.PLATFORM,
    label: "Platform",
    value: FIREWATCH_SEARCH_PARAMS.PLATFORM,
  };

  // const stateFilterGroup: FilterMenuOption = {
  //   field: "state",
  //   label: "State",
  //   value: "state",
  // };

  const queryFilterGroup: FilterMenuOption = {
    field: FIREWATCH_SEARCH_PARAMS.QUERY_TERM,
    label: `Press enter to free text search "${searchInput}"`,
    value: FIREWATCH_SEARCH_PARAMS.QUERY_TERM,
    isDisabled: true,
  };

  const isInitiative = (baseQueryFilters?.initiativeMrns?.length ?? 0) > 0;
  const shouldShowTypeFilter =
    (pageType === "AllSecurityFindings" || pageType === "AssetAllFindings") &&
    !isInitiative;

  const suggestions = [
    searchInput.length > 0 ? queryFilterGroup : undefined,
    shouldShowTypeFilter ? typeFilterGroup : undefined,
    riskRatingFilterGroup,
    riskFactorFilterGroup,
    platformFilterGroup,
    pageType === "AllSecurityFindings" ||
    pageType === "AssetAllFindings" ||
    pageType === AggregateScoreType.Check
      ? policyFilterGroup
      : undefined,
    // stateFilterGroup,
  ]
    .flatMap((g) => g ?? [])
    .filter((s) =>
      s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
    );

  const loading = false;
  const error = false;

  return {
    suggestions,
    loading,
    error,
  };
}

function useFirewatchTypeSuggestions(
  searchInput: string,
  pageType: AdjustedAggregateScoreType,
) {
  const suggestions = (
    pageType === "AllSecurityFindings" || pageType === "AssetAllFindings"
      ? [
          {
            field: FIREWATCH_SEARCH_PARAMS.TYPE,
            label: "Vulnerability",
            value: AggregateScoreType.Vulnerability,
            isMultiple: true,
            param: FIREWATCH_SEARCH_PARAMS.TYPE,
          },
          {
            field: FIREWATCH_SEARCH_PARAMS.TYPE,
            label: "Advisory",
            value: AggregateScoreType.Advisory,
            isMultiple: true,
            param: FIREWATCH_SEARCH_PARAMS.TYPE,
          },
          {
            field: FIREWATCH_SEARCH_PARAMS.TYPE,
            label: "Check",
            value: AggregateScoreType.Check,
            isMultiple: true,
            param: FIREWATCH_SEARCH_PARAMS.TYPE,
          },
        ]
      : []
  ).filter((s) =>
    s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
  );

  const loading = false;
  const error = false;

  return {
    suggestions,
    loading,
    error,
  };
}

function useFirewatchRiskRatingSuggestions(
  entityMrn: string,
  baseQueryFilters: GetAggregateScoresQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetAggregateScoresSuggestionsLazyQuery({
      fetchPolicy: "cache-and-network",
    });

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AggScoresFilterType.Rating,
        searchText,
        entityMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const loadingSuggestion = loadingMenuOption(
    FIREWATCH_SEARCH_PARAMS.RISK_RATING,
    "riskRating",
  );
  const emptySuggestion = emptyMenuOption(
    FIREWATCH_SEARCH_PARAMS.RISK_RATING,
    "riskRating",
  );

  const suggestionsData =
    (data ?? previousData)?.aggregateScoresSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "AggregateScoreRatingValue"
            ? suggestion.value.ratingValue
            : "";
        return {
          param: FIREWATCH_SEARCH_PARAMS.RISK_RATING,
          field: `riskRating.${index}`,
          value: value,
          label: formatRiskRatingLabel(suggestion.label),
          isMultiple: true,
        };
      },
    ) || [];

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    fetchSuggestions,
    suggestions,
    loading,
    error,
  };
}

function useFirewatchRiskFactorSuggestions(
  scopeMrn: string,
  pageType: AdjustedAggregateScoreType,
  baseQueryFilters: GetAggregateScoresQueryVariables["filter"],
) {
  const findingMrn = findingMrnForPageType(pageType);

  const [getSuggestions, { data, previousData, loading, error }] =
    useGetAggregateScoresSuggestionsLazyQuery({
      fetchPolicy: "cache-and-network",
    });

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AggScoresFilterType.RiskFactors,
        searchText,
        entityMrn: scopeMrn,
        filter: {
          findingMrn,
          ...baseQueryFilters,
        },
      },
    });
  };

  const loadingSuggestion = loadingMenuOption(
    FIREWATCH_SEARCH_PARAMS.RISK_FACTOR_MRN,
    "riskFactorMrn",
  );
  const emptySuggestion = emptyMenuOption(
    FIREWATCH_SEARCH_PARAMS.RISK_FACTOR_MRN,
    "riskFactorMrn",
  );

  const suggestionsData =
    (data ?? previousData)?.aggregateScoresSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param: FIREWATCH_SEARCH_PARAMS.RISK_FACTOR_MRN,
          field: `riskFactorMrn.${index}`,
          value: value,
          label: suggestion.label,
          isMultiple: true,
        };
      },
    ) || [];

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useFirewatchPolicySuggestions(
  scopeMrn: string,
  pageType: AdjustedAggregateScoreType,
  baseQueryFilters: GetAggregateScoresQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetAggregateScoresSuggestionsLazyQuery({
      fetchPolicy: "cache-and-network",
    });

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AggScoresFilterType.PolicyMrn,
        searchText,
        entityMrn: scopeMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const loadingSuggestion = loadingMenuOption(
    FIREWATCH_SEARCH_PARAMS.POLICY_MRN,
    "policyMrn",
  );
  const emptySuggestion = emptyMenuOption(
    FIREWATCH_SEARCH_PARAMS.POLICY_MRN,
    "policyMrn",
  );

  const suggestionsData =
    (data ?? previousData)?.aggregateScoresSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param: FIREWATCH_SEARCH_PARAMS.POLICY_MRN,
          field: `policyMrn.${index}`,
          value: value,
          label: suggestion.label,
        };
      },
    ) || [];

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useFirewatchStateSuggestions(
  scopeMrn: string,
  baseQueryFilters: GetAggregateScoresQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetAggregateScoresSuggestionsLazyQuery({
      fetchPolicy: "cache-and-network",
    });

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AggScoresFilterType.State,
        searchText,
        entityMrn: scopeMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const suggestionsData =
    (data ?? previousData)?.aggregateScoresSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "AggregateScoreStateValue"
            ? suggestion.value.stateValue
            : "";
        return {
          param: FIREWATCH_SEARCH_PARAMS.STATE,
          field: `state.${index}`,
          value: value,
          label: formatStateLabel(suggestion.label),
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(
    FIREWATCH_SEARCH_PARAMS.STATE,
    "state",
  );
  const emptySuggestion = emptyMenuOption(
    FIREWATCH_SEARCH_PARAMS.STATE,
    "state",
  );

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useFirewatchPlatformSuggestions(
  scopeMrn: string,
  baseQueryFilters: GetAggregateScoresQueryVariables["filter"],
) {
  const [getSuggestions, { data, previousData, loading, error }] =
    useGetAggregateScoresSuggestionsLazyQuery({
      fetchPolicy: "cache-and-network",
    });

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AggScoresFilterType.Platform,
        searchText,
        entityMrn: scopeMrn,
        filter: baseQueryFilters,
      },
    });
  };

  const suggestionsData =
    (data ?? previousData)?.aggregateScoresSuggestions
      ?.flatMap((suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param: FIREWATCH_SEARCH_PARAMS.PLATFORM,
          field: `platform.${index}`,
          value,
          label: suggestion.label,
          isMultiple: true,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const loadingSuggestion = loadingMenuOption(
    FIREWATCH_SEARCH_PARAMS.PLATFORM,
    "platform.loading",
  );
  const emptySuggestion = emptyMenuOption(
    FIREWATCH_SEARCH_PARAMS.PLATFORM,
    "platform.empty",
  );

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return { suggestions, fetchSuggestions, loading, error };
}

function findingMrnForPageType(pageType: AdjustedAggregateScoreType) {
  switch (pageType) {
    case AggregateScoreType.Check:
      return "//security.api.mondoo.app/findings/security";
    // These findingMrns aren't working. Use /all for now.
    // case AggregateScoreType.Vulnerability:
    //   return "//security.api.mondoo.app/findings/cves"
    // case AggregateScoreType.Advisory:
    //   return "//security.api.mondoo.app/findings/advisories"
    // case AggregateScoreType.Software:
    //   return "//security.api.mondoo.app/findings/software"
    // case AggregateScoreType.Asset:
    //   return "//security.api.mondoo.app/assets/vulns"
    case "AllSecurityFindings":
    case "AssetAllFindings":
    default:
      return "//security.api.mondoo.app/findings/all";
  }
}

function formatTypeLabel(label: string) {
  return upperFirst(label.toLocaleLowerCase());
}

function isScoreRating(value: any): value is ScoreRating {
  return Object.values(ScoreRating).includes(value);
}

function formatRiskRatingLabel(label: string) {
  return upperFirst(label.toLocaleLowerCase());
}

function formatRiskFactorLabel(label: string) {
  return label.split("/").pop() || label;
}

function formatPolicyLabel(label: string) {
  return label.split("/").pop() || label;
}

function formatStateLabel(label: string) {
  return (
    label
      .toLowerCase()
      .split("_")
      .map((s) => upperFirst(s))
      .join(" ") || label
  );
}

function isAggregateScoreState(value: any): value is AggregateScoreState {
  return Object.values(AggregateScoreState).includes(value);
}
